/** Isolated S012 chat-list effects. */
import type { ChatListMsg } from './messages';
import { copyConversationSummaries } from './model';
import type { ChatListPort } from './port';

export type ChatListEffect = {
  readonly type: 'LoadConversations';
  readonly loadId: number;
};

export interface ChatListEffectContext {
  readonly dispatch: (message: ChatListMsg) => void;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Run one load command. Exceptions are classified, never copied into state or
 * rendered. Cancellation is silent and cannot dispatch a completion.
 */
export async function runChatListEffect(
  effect: ChatListEffect,
  context: ChatListEffectContext,
  port: ChatListPort,
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted) {
    return;
  }

  try {
    const result = await port.loadConversations(signal);
    if (signal.aborted) {
      return;
    }

    if (result.type === 'inactive_sponsor') {
      context.dispatch({
        type: 'LoadInactiveSponsor',
        loadId: effect.loadId,
      });
      return;
    }

    // Treat the port boundary as untrusted at runtime. This validation also
    // gives the reducer an immutable, caller-independent snapshot.
    const conversations = copyConversationSummaries(result.conversations);
    context.dispatch({
      type: 'LoadSucceeded',
      loadId: effect.loadId,
      conversations,
    });
  } catch (error) {
    if (signal.aborted || isAbortError(error)) {
      return;
    }
    context.dispatch({ type: 'LoadFailed', loadId: effect.loadId });
  }
}
